<template>
  <div class="tags-selecao">

    <!-- Header: busca + contador + filtro -->
    <div class="tags-header">
      <div class="search-wrapper">
        <fa-icon icon="search" class="search-icon" />
        <input
          class="form-control"
          type="text"
          v-model="filter"
          placeholder="Pesquisar tag..."
        />
      </div>
      <span class="tags-counter">
        <strong>{{ tagsSelecionadas.length }}</strong> / {{ tags.length }} selecionadas
      </span>
      <label class="filter-toggle">
        <input type="checkbox" v-model="filterSelecionadas" />
        Apenas selecionadas
      </label>
    </div>

    <!-- Alerta informativo -->
    <div class="tags-alert" v-if="tagsSelecionadas.length > 1">
      <fa-icon icon="info-circle" class="alert-icon" />
      <span>
        Tags sao combinadas com logica "E". Certifique-se de que as tags selecionadas sejam compativeis entre si.
      </span>
    </div>

    <!-- Lista de tags em cards -->
    <div class="tags-list" v-if="tagsFiltradas.length > 0">
      <div
        v-for="tag in tagsFiltradas"
        :key="tag.id"
        class="tag-card"
        :class="{ active: isSelected(tag) }"
        @click="toggle(tag)"
      >
        <div class="tag-card-main">
          <div class="tag-card-icon">
            <fa-icon :icon="isSelected(tag) ? 'check' : 'filter'" />
          </div>
          <div class="tag-card-info">
            <div class="tag-card-name">
              {{ tag.titulo }}
              <span v-if="tag.chave === null" class="badge-custom">Personalizada</span>
              <span v-if="hasParams(tag)" class="badge-params">Dinamica</span>
            </div>
            <div class="tag-card-desc" v-if="tag.descricao">{{ tag.descricao }}</div>
          </div>
        </div>

        <!-- Formulario de parametros (expandido quando selecionada e tem params) -->
        <div
          v-if="isSelected(tag) && hasParams(tag)"
          class="tag-params-form"
          @click.stop
        >
          <div class="params-label">PARAMETROS DA TAG</div>
          <div class="params-grid">
            <div
              v-for="param in getTagParams(tag)"
              :key="param.nome"
              class="param-field"
              :class="{ 'param-full': getTagParams(tag).length === 1 }"
            >
              <!-- Select (opcoes carregadas da API) -->
              <template v-if="param.tipo === 'select'">
                <label>{{ param.label }}</label>
                <select
                  v-if="!param.multiplo"
                  class="form-control form-control-sm"
                  :value="getParamValue(tag, param.nome)"
                  @change="setParamValue(tag, param.nome, $event.target.value)"
                >
                  <option value="">Selecione</option>
                  <option
                    v-for="opt in getSelectOptions(param)"
                    :key="opt.value"
                    :value="opt.value"
                  >{{ opt.label }}</option>
                </select>
                <div v-else class="multi-select-container">
                  <div class="multi-select-tags">
                    <span
                      v-for="val in (getParamValue(tag, param.nome) || [])"
                      :key="val"
                      class="multi-select-tag"
                    >
                      {{ getSelectLabel(param, val) }}
                      <span class="multi-select-tag-remove" @click.stop="removeMultiValue(tag, param.nome, val)">&times;</span>
                    </span>
                    <span v-if="!(getParamValue(tag, param.nome) || []).length" class="multi-select-placeholder">Selecione</span>
                  </div>
                  <div class="multi-select-options">
                    <label
                      v-for="opt in getSelectOptions(param)"
                      :key="opt.value"
                      class="multi-select-option"
                      @click.stop
                    >
                      <input
                        type="checkbox"
                        :checked="(getParamValue(tag, param.nome) || []).includes(opt.value)"
                        @change="toggleMultiValue(tag, param.nome, opt.value)"
                      />
                      {{ opt.label }}
                    </label>
                  </div>
                </div>
              </template>

              <!-- Boolean -->
              <template v-else-if="param.tipo === 'boolean'">
                <label>{{ param.label }}</label>
                <select
                  class="form-control form-control-sm"
                  :value="getParamValue(tag, param.nome)"
                  @change="setParamValue(tag, param.nome, $event.target.value)"
                >
                  <option value="">Selecione</option>
                  <option value="true">Sim</option>
                  <option value="false">Nao</option>
                </select>
              </template>

              <!-- Number -->
              <template v-else-if="param.tipo === 'number'">
                <label>{{ param.label }}</label>
                <input
                  type="number"
                  class="form-control form-control-sm"
                  :value="getParamValue(tag, param.nome)"
                  @input="setParamValue(tag, param.nome, $event.target.value)"
                  placeholder="Informe o valor"
                />
              </template>

              <!-- Date -->
              <template v-else-if="param.tipo === 'date'">
                <label>{{ param.label }}</label>
                <input
                  type="date"
                  class="form-control form-control-sm"
                  :value="getParamValue(tag, param.nome)"
                  @input="setParamValue(tag, param.nome, $event.target.value)"
                />
              </template>

              <!-- Text (default) -->
              <template v-else>
                <label>{{ param.label }}</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :value="getParamValue(tag, param.nome)"
                  @input="setParamValue(tag, param.nome, $event.target.value)"
                  placeholder="Informe o valor"
                />
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div class="tags-empty" v-else>
      <fa-icon icon="filter" class="empty-icon" />
      <p v-if="filter">Nenhuma tag encontrada com o termo <strong>"{{ filter }}"</strong></p>
      <p v-else>Nenhuma tag disponivel</p>
    </div>

  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import { arrayFilter } from "@/helpers/utils";
import _findIndex from "lodash/findIndex";

function extractTagParams(condicoes) {
  if (!condicoes || !condicoes.grupos) return [];

  const params = [];

  for (const grupo of condicoes.grupos) {
    for (const condicao of grupo.condicoes || []) {
      if ((condicao.tipo_valor || "static") === "parametro") {
        const param = condicao.parametro || {};
        const campoParts = (condicao.campo || "").split(".");
        params.push({
          nome: param.nome || condicao.campo,
          label: param.label || condicao.campo,
          tipo: param.tipo || "text",
          campo: condicao.campo,
          multiplo: param.multiplo || false,
          entidade: (campoParts[0] || "").toLowerCase(),
          opcoes_campo: (campoParts[1] || "").toLowerCase(),
        });
      }
    }
  }

  return params;
}

export default {
  name: "TabelaSelecaoTagFiltro",
  props: ["entidade", "value"],
  data() {
    return {
      filter: "",
      filterSelecionadas: false,
      tags: [],
      tagsSelecionadas: [],
      selectOptionsCache: {},
    };
  },
  computed: {
    tagsFiltradas() {
      let filtered = arrayFilter(this.tags, this.filter, ["titulo", "descricao"]);

      if (this.filterSelecionadas) {
        filtered = filtered.filter((tag) => this.isSelected(tag));
      }

      return filtered;
    },
  },
  methods: {
    loadTags() {
      get(`/admin/tags-filtro?entidade=${this.entidade}`)
        .then((json) => { this.tags = json; });
    },

    isSelected(tag) {
      return _findIndex(this.tagsSelecionadas, (s) => s.id === tag.id) >= 0;
    },

    toggle(tag) {
      const idx = _findIndex(this.tagsSelecionadas, (s) => s.id === tag.id);

      if (idx < 0) {
        // Ao selecionar, preservar parametros existentes (se editando)
        const existing = (this.value || []).find((t) => t.id === tag.id);
        const entry = {
          id: tag.id,
          titulo: tag.titulo,
          descricao: tag.descricao,
          condicoes: tag.condicoes,
          parametros: (existing && existing.parametros) ? existing.parametros : {},
        };
        this.tagsSelecionadas.push(entry);

        // Pre-carregar opcoes dos selects se a tag tem params do tipo select
        const params = extractTagParams(tag.condicoes);
        params.forEach((p) => {
          if (p.tipo === "select" && p.entidade && p.opcoes_campo) {
            this.loadSelectOptions(p);
          }
        });
      } else {
        this.tagsSelecionadas.splice(idx, 1);
      }
    },

    hasParams(tag) {
      return extractTagParams(tag.condicoes).length > 0;
    },

    getTagParams(tag) {
      return extractTagParams(tag.condicoes);
    },

    getParamValue(tag, nome) {
      const sel = this.tagsSelecionadas.find((s) => s.id === tag.id);
      if (!sel || !sel.parametros) return "";
      return sel.parametros[nome] || "";
    },

    setParamValue(tag, nome, valor) {
      const sel = this.tagsSelecionadas.find((s) => s.id === tag.id);
      if (!sel) return;

      if (!sel.parametros) {
        this.$set(sel, "parametros", {});
      }
      this.$set(sel.parametros, nome, valor);
    },

    // Multiselect helpers
    toggleMultiValue(tag, nome, value) {
      const sel = this.tagsSelecionadas.find((s) => s.id === tag.id);
      if (!sel) return;
      if (!sel.parametros) this.$set(sel, "parametros", {});
      const current = sel.parametros[nome] || [];
      const arr = Array.isArray(current) ? [...current] : [];
      const idx = arr.indexOf(value);
      if (idx >= 0) {
        arr.splice(idx, 1);
      } else {
        arr.push(value);
      }
      this.$set(sel.parametros, nome, arr);
    },

    removeMultiValue(tag, nome, value) {
      this.toggleMultiValue(tag, nome, value);
    },

    getSelectLabel(param, value) {
      const opts = this.getSelectOptions(param);
      const opt = opts.find((o) => o.value === value);
      return opt ? opt.label : value;
    },

    // Cache de opcoes para selects dinamicos
    getSelectOptions(param) {
      const key = `${param.entidade}__${param.opcoes_campo}`;
      return this.selectOptionsCache[key] || [];
    },

    loadSelectOptions(param) {
      const key = `${param.entidade}__${param.opcoes_campo}`;
      if (this.selectOptionsCache[key]) return;

      // Setar array vazio para evitar chamadas duplicadas
      this.$set(this.selectOptionsCache, key, []);

      get(`/admin/tag-filtro/opcoes/${param.entidade}/${param.opcoes_campo}`)
        .then((json) => {
          const opcoes = (json || []).map((item) => ({
            value: typeof item === "object" ? item.value : item,
            label: typeof item === "object" ? item.label : item,
          }));
          this.$set(this.selectOptionsCache, key, opcoes);
        })
        .catch(() => {});
    },

    initFromValue() {
      this.tagsSelecionadas = this.value || [];

      // Pre-carregar opcoes para tags selecionadas com params do tipo select
      this.tagsSelecionadas.forEach((tag) => {
        const params = extractTagParams(tag.condicoes);
        params.forEach((p) => {
          if (p.tipo === "select" && p.entidade && p.opcoes_campo) {
            this.loadSelectOptions(p);
          }
        });
      });
    },
  },
  watch: {
    tagsSelecionadas: {
      deep: true,
      handler(val) {
        this.$emit("input", val);
      },
    },
    value(val) {
      // Se o pai resetar o valor (ex: mudar tipo_regra), sincronizar
      if (Array.isArray(val) && val.length === 0 && this.tagsSelecionadas.length > 0) {
        this.tagsSelecionadas = [];
      }
    },
  },
  beforeMount() {
    this.loadTags();
  },
  mounted() {
    this.initFromValue();
  },
};
</script>

<style scoped>
.tags-selecao {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ===== HEADER ===== */
.tags-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.search-wrapper {
  position: relative;
  flex: 0 0 220px;
}

.search-wrapper .search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #98a2b3;
  font-size: 13px;
}

.search-wrapper .form-control {
  padding-left: 32px;
  border-radius: 8px;
  font-size: 13px;
  height: 36px;
}

.tags-counter {
  font-size: 13px;
  color: #667085;
  white-space: nowrap;
}

.tags-counter strong {
  color: #3a3e81;
}

.filter-toggle {
  font-size: 13px;
  color: #667085;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  margin: 0;
  white-space: nowrap;
}

.filter-toggle input[type="checkbox"] {
  accent-color: #3a3e81;
}

/* ===== ALERT ===== */
.tags-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #eff8ff;
  border: 1px solid #b2ddff;
  border-radius: 8px;
  font-size: 12px;
  color: #1849a9;
}

.tags-alert .alert-icon {
  color: #2e90fa;
  font-size: 14px;
  flex-shrink: 0;
}

/* ===== TAG LIST (cards como regra fixa) ===== */
.tags-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 420px;
  overflow-y: auto;
}

.tag-card {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  background: #fff;
  overflow: hidden;
  flex-shrink: 0;
}

.tag-card:hover {
  border-color: #b0bec5;
  background: #fafbfc;
}

.tag-card.active {
  border-color: #3a3e81;
  background: #eceaf3;
}

.tag-card-main {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
}

.tag-card-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #f1f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667085;
  font-size: 16px;
  flex-shrink: 0;
  transition: all 0.15s;
}

.tag-card.active .tag-card-icon {
  background: #e3e3ee;
  color: #3a3e81;
}

.tag-card-info {
  flex: 1;
  min-width: 0;
}

.tag-card-name {
  font-weight: 600;
  font-size: 14px;
  color: #1d2939;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.badge-custom {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #d0d5dd;
  color: #667085;
  background: #f9fafb;
}

.badge-params {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  background: #eff8ff;
  color: #1570ef;
  border: 1px solid #b2ddff;
}

.tag-card-desc {
  font-size: 12px;
  color: #667085;
  margin-top: 2px;
  line-height: 1.4;
}

/* ===== PARAMS FORM (expandido dentro do card) ===== */
.tag-params-form {
  border-top: 1px solid #e4e7eb;
  background: #f8f9fa;
  padding: 14px 16px;
  cursor: default;
}

.params-label {
  font-size: 11px;
  font-weight: 700;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}

.params-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.param-field {
  flex: 0 0 calc(50% - 6px);
  min-width: 0;
}

.param-field.param-full {
  flex: 0 0 100%;
}

.param-field label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 4px;
}

.param-field .form-control-sm {
  font-size: 13px;
  border-radius: 6px;
}

/* ===== MULTI SELECT ===== */
.multi-select-container {
  border: 1px solid #ced4da;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
}

.multi-select-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 8px;
  min-height: 34px;
  align-items: center;
}

.multi-select-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #e3e3ee;
  color: #3a3e81;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.multi-select-tag-remove {
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  opacity: 0.7;
}

.multi-select-tag-remove:hover {
  opacity: 1;
}

.multi-select-placeholder {
  color: #98a2b3;
  font-size: 13px;
}

.multi-select-options {
  border-top: 1px solid #e9ecef;
  max-height: 150px;
  overflow-y: auto;
  padding: 4px 0;
}

.multi-select-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  font-size: 13px;
  color: #344054;
  cursor: pointer;
  margin: 0;
  transition: background 0.1s;
}

.multi-select-option:hover {
  background: #f9fafb;
}

.multi-select-option input[type="checkbox"] {
  accent-color: #3a3e81;
}

/* ===== EMPTY STATE ===== */
.tags-empty {
  text-align: center;
  padding: 40px 20px;
  color: #98a2b3;
}

.tags-empty .empty-icon {
  font-size: 36px;
  color: #d0d5dd;
  margin-bottom: 10px;
}

.tags-empty p {
  font-size: 13px;
  margin: 0;
}
</style>
